import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { ETLayout, euro } from "@/pages/et/ETLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ManualeButton } from "@/components/ManualeButton";
import { Search, FileText, Mail, Loader2, CheckCircle2, AlertTriangle, Send } from "lucide-react";

export default function ETRicevute() {
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [emailCfg, setEmailCfg] = useState({ configured: false, sender: "" });
  const [sendRic, setSendRic] = useState(null);
  const [sendTo, setSendTo] = useState("");
  const [sending, setSending] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkSending, setBulkSending] = useState(false);
  const [onlyPending, setOnlyPending] = useState(false);
  const [sentToday, setSentToday] = useState(null);

  const load = () => api.get("/et/ricevute", { params: query ? { query } : {} }).then(({ data }) => setItems(data));
  const loadToday = () => api.get("/et/ricevute/inviate-oggi").then(({ data }) => setSentToday(data)).catch(() => {});
  useEffect(() => { load(); loadToday(); }, []); // eslint-disable-line
  useEffect(() => { api.get("/et/email/status").then(({ data }) => setEmailCfg(data)).catch(() => {}); }, []);

  const visible = onlyPending ? items.filter((r) => !r.inviata) : items;
  const pendingCount = items.filter((r) => !r.inviata).length;

  const toggleOne = (id) => setSelected((prev) => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const allVisibleSelected = visible.length > 0 && visible.every((r) => selected.has(r.ricevuta_id));
  const toggleAll = () => setSelected((prev) => {
    const n = new Set(prev);
    if (visible.every((r) => n.has(r.ricevuta_id))) visible.forEach((r) => n.delete(r.ricevuta_id));
    else visible.forEach((r) => n.add(r.ricevuta_id));
    return n;
  });

  const doBulk = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    setBulkSending(true);
    try {
      const { data } = await api.post("/et/ricevute/invia-massivo", { ricevuta_ids: ids });
      let msg = `Inviate ${data.inviate}/${data.totale} ricevute`;
      if (data.senza_email?.length) msg += ` · ${data.senza_email.length} senza email`;
      if (data.errori?.length) msg += ` · ${data.errori.length} errori`;
      if (data.errori?.length || data.senza_email?.length) toast.warning(msg);
      else toast.success(msg);
      setSelected(new Set());
      load(); loadToday();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBulkSending(false); }
  };

  const openSend = async (r) => {
    setSendRic(r);
    let to = r.email_destinatario || "";
    if (!to && r.ben_id) {
      try { const { data } = await api.get(`/et/beneficiari/${r.ben_id}`); to = data.email || ""; } catch { /* noop */ }
    }
    setSendTo(to);
  };

  const doSend = async () => {
    if (!sendRic) return;
    setSending(true);
    try {
      const { data } = await api.post(`/et/ricevute/${sendRic.ricevuta_id}/invia-email`, { email: sendTo });
      toast.success(`Ricevuta inviata a ${data.email}`);
      setSendRic(null); load(); loadToday();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <ETLayout subtitle="Documenti" title="Ricevute" actions={<ManualeButton route="/manuale/easytrasporto" />}>
      {emailCfg.configured ? (
        <div className="mb-4 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-sm text-emerald-800 flex items-center gap-2" data-testid="et-email-ok">
          <CheckCircle2 className="h-4 w-4" /> Invio email attivo · mittente <b>{emailCfg.sender}</b>
        </div>
      ) : (
        <div className="mb-4 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800 flex items-center gap-2" data-testid="et-email-off">
          <AlertTriangle className="h-4 w-4" /> Casella email non ancora collegata. L'invio si attiverà quando verrà inserita la Password per le app della casella Anffas.
        </div>
      )}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Cerca per beneficiario o numero…" className="pl-9 bg-white border-slate-300" data-testid="et-ric-search" />
        </div>
        <div className="inline-flex rounded-lg border border-slate-300 bg-white p-0.5 self-start" data-testid="et-ric-filter">
          <button type="button" onClick={() => setOnlyPending(false)} className={`px-3 py-1.5 text-sm rounded-md transition-colors ${!onlyPending ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`} data-testid="et-filter-all">Tutte</button>
          <button type="button" onClick={() => setOnlyPending(true)} className={`px-3 py-1.5 text-sm rounded-md transition-colors ${onlyPending ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`} data-testid="et-filter-pending">Solo da inviare{pendingCount ? ` (${pendingCount})` : ""}</button>
        </div>
        {sentToday && (
          <div className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium self-start ${sentToday.oggi >= sentToday.consigliato ? "bg-red-50 text-red-700 border border-red-200" : "bg-slate-100 text-slate-700 border border-slate-200"}`} data-testid="et-sent-today">
            <Send className="h-3.5 w-3.5" /> Inviate oggi: {sentToday.oggi}/{sentToday.consigliato}
          </div>
        )}
      </div>
      {selected.size > 0 && (
        <div className="mb-3 flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3" data-testid="et-bulk-bar">
          <div className="flex items-center gap-2 text-sm text-indigo-900">
            <span className="font-medium">{selected.size}</span> ricevute selezionate
            <span className="flex items-center gap-1 text-amber-700 ml-1">
              <AlertTriangle className="h-3.5 w-3.5" /> Consigliato: max 10-15 invii al giorno per evitare lo spam.
            </span>
          </div>
          <div className="sm:ml-auto flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setSelected(new Set())} disabled={bulkSending} data-testid="et-bulk-clear">Deseleziona</Button>
            <Button size="sm" className="brand-gradient text-white border-0" onClick={doBulk} disabled={bulkSending || !emailCfg.configured} data-testid="et-bulk-send">
              {bulkSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-3.5 w-3.5 mr-1" /> Invia {selected.size} selezionate</>}
            </Button>
          </div>
        </div>
      )}
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead className="w-10"><Checkbox checked={allVisibleSelected} onCheckedChange={toggleAll} aria-label="Seleziona tutto" data-testid="et-select-all" /></TableHead>
            <TableHead>Numero</TableHead><TableHead>Beneficiario</TableHead><TableHead>Comp.</TableHead>
            <TableHead>Importo</TableHead><TableHead>Data</TableHead><TableHead>Email</TableHead><TableHead className="text-right">Azioni</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {visible.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center text-slate-400 py-10">{onlyPending ? "Nessuna ricevuta da inviare." : "Nessuna ricevuta."}</TableCell></TableRow>
            ) : visible.map((r) => (
              <TableRow key={r.ricevuta_id} data-testid={`et-ric-row-${r.ricevuta_id}`}>
                <TableCell><Checkbox checked={selected.has(r.ricevuta_id)} onCheckedChange={() => toggleOne(r.ricevuta_id)} aria-label={`Seleziona ${r.numero}`} data-testid={`et-select-${r.ricevuta_id}`} /></TableCell>
                <TableCell className="font-mono text-xs">{r.numero}</TableCell>
                <TableCell className="text-sm text-slate-900">{r.ben_name}</TableCell>
                <TableCell className="text-sm">{r.competenza}</TableCell>
                <TableCell className="text-sm font-medium">{euro(r.importo)}</TableCell>
                <TableCell className="text-sm text-slate-500">{r.data}</TableCell>
                <TableCell>{r.inviata ? <Badge className="bg-emerald-100 text-emerald-700 border-0">Inviata</Badge> : <Badge variant="outline" className="text-slate-500">Da inviare</Badge>}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="sm" variant="outline" className="mr-1" onClick={() => window.open(`${API}/et/ricevute/${r.ricevuta_id}/pdf`, "_blank")} data-testid={`et-ric-pdf-${r.ricevuta_id}`}>
                    <FileText className="h-3.5 w-3.5 mr-1" /> Apri
                  </Button>
                  <Button size="sm" className="brand-gradient text-white border-0" onClick={() => openSend(r)} data-testid={`et-ric-email-${r.ricevuta_id}`}>
                    <Mail className="h-3.5 w-3.5 mr-1" /> Invia
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!sendRic} onOpenChange={(o) => !o && setSendRic(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Invia ricevuta via email</DialogTitle></DialogHeader>
          {sendRic && (
            <div className="py-2 space-y-3">
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm space-y-1">
                <div className="flex justify-between"><span className="text-slate-500">Ricevuta</span><span className="font-medium">{sendRic.numero}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Beneficiario</span><span className="font-medium">{sendRic.ben_name}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Importo</span><span className="font-medium">{euro(sendRic.importo)}</span></div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-600 text-sm">Email destinatario</Label>
                <Input type="email" value={sendTo} onChange={(e) => setSendTo(e.target.value)} placeholder="email del pagante…" data-testid="et-send-to" />
              </div>
              {!emailCfg.configured && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-2 text-xs text-amber-800 flex items-center gap-1">
                  <AlertTriangle className="h-3.5 w-3.5" /> La casella non è ancora collegata: l'invio fallirà finché non verrà inserita la Password per le app.
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendRic(null)}>Annulla</Button>
            <Button onClick={doSend} disabled={sending || !sendTo} className="brand-gradient text-white border-0" data-testid="et-send-confirm">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Mail className="h-4 w-4 mr-2" /> Invia ricevuta</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ETLayout>
  );
}
